import { useEffect, useRef, useState } from 'react'
import { ENCODING, ENCODING_OPTIONS, TEMPLATE_FILES_TYPES } from './constant'
import { RootStateOrAny, useSelector } from 'react-redux'
import { ApiPostEventsType } from 'core/api/events'
import { useLocation } from 'react-router'
import { ItemData } from '../../core/models/Template'
import helpers from 'core/helpers'

type Props = {
    uploadMethod: ApiPostEventsType | string | null
    headers?: { [key: string]: string }
    onSuccess?: (response: { body: unknown }) => void
    isSelectFromFile?: boolean
    selectedItems?: ItemData[]
}

const parseResponseBody = async (res: Response): Promise<unknown> => {
    if (!res.headers.get('content-type')?.includes('application/json')) {
        return null
    }

    try {
        return await res.json()
    } catch {
        return null
    }
}

const CSV_ACTION_TYPES = [
    TEMPLATE_FILES_TYPES.IPTV,
    TEMPLATE_FILES_TYPES.WINE_CATALOG,
    TEMPLATE_FILES_TYPES.MENU_PRODUCT_CATALOG,
    TEMPLATE_FILES_TYPES.COMMON_FAVOURITE_LINKS_CATALOG,
    TEMPLATE_FILES_TYPES.SCHOOL_SCHEDULE,
]

const LOCATION_ACTION_TYPE_MAP: { [key: string]: TEMPLATE_FILES_TYPES } = {
    '/schedule/add': TEMPLATE_FILES_TYPES.SELECT_FROM_FILE,
    '/schedule/edit': TEMPLATE_FILES_TYPES.SELECT_FROM_FILE,
    '/ipTv': TEMPLATE_FILES_TYPES.IPTV,
    '/instancesCatalog/courses': TEMPLATE_FILES_TYPES.COURSES_CATALOG,
    '/instancesCatalog/classrooms': TEMPLATE_FILES_TYPES.CLASSROOMS_CATALOG,
    '/instancesCatalog/schoolSchedule': TEMPLATE_FILES_TYPES.SCHOOL_SCHEDULE,
    '/instancesCatalog/grades': TEMPLATE_FILES_TYPES.GRADES_CATALOG,
    '/instancesCatalog/teachers': TEMPLATE_FILES_TYPES.TEACHERS_CATALOG,
    '/instancesCatalog/wine': TEMPLATE_FILES_TYPES.WINE_CATALOG,
    '/instancesCatalog/menuProduct': TEMPLATE_FILES_TYPES.MENU_PRODUCT_CATALOG,
    '/instancesCatalog/catalogizator': TEMPLATE_FILES_TYPES.COMMON_FAVOURITE_LINKS_CATALOG,
    '/instancesCatalog/subjects': TEMPLATE_FILES_TYPES.SUBJECTS_CATALOG,
}

const INSTANCE_BY_ACTION_TYPE: Partial<Record<
    TEMPLATE_FILES_TYPES,
    'course' | 'grade' | 'classroom' | 'teacher' | 'subject'
>> = {
    [TEMPLATE_FILES_TYPES.COURSES_CATALOG]: 'course',
    [TEMPLATE_FILES_TYPES.GRADES_CATALOG]: 'grade',
    [TEMPLATE_FILES_TYPES.CLASSROOMS_CATALOG]: 'classroom',
    [TEMPLATE_FILES_TYPES.TEACHERS_CATALOG]: 'teacher',
    [TEMPLATE_FILES_TYPES.SUBJECTS_CATALOG]: 'subject',
}

const useUpload = ({ uploadMethod, headers, onSuccess, isSelectFromFile, selectedItems }: Props) => {
    const [loading, setLoading] = useState(false)
    const tokens: any = useSelector<RootStateOrAny>((state) => state.user.tokens)
    const [files, setFiles] = useState<File[]>([])
    const [encoding, setEncoding] = useState<ENCODING>(ENCODING_OPTIONS[0].id)
    const [divider, setDivider] = useState(',')
    const uploadRef = useRef<HTMLInputElement>(null)
    const location = useLocation()
    const [actionType, setActionType] = useState<TEMPLATE_FILES_TYPES | null>(null)
    const [uploadTypeValue, setUploadTypeValue] = useState<string>('replaceSelected')

    useEffect(() => {
        const actionType = LOCATION_ACTION_TYPE_MAP[location.pathname]
        if (actionType) {
            setActionType(actionType)
        }
    }, [location])

    const acceptFiles = CSV_ACTION_TYPES.includes(actionType as TEMPLATE_FILES_TYPES) ? '.csv' : '.xlsx'

    const addFiles = (file: File[]) => {
        setFiles(file)
    }

    const deleteFile = (fileName: string) => {
        setFiles((prev) => prev.filter((file) => file.name !== fileName))
    }

    const handleSubmit = () => {
        sendData(files)
    }

    const handleSelectEncoding = (selectedEncoding: { id: ENCODING; name: string }) => {
        setEncoding(selectedEncoding.id)
    }

    const sendData = async (files: any) => {
        if (!files || !uploadMethod || !actionType) return

        const file = files[0]

        setLoading(true)
        const formData = new FormData()
        if (CSV_ACTION_TYPES.includes(actionType)) {
            formData.append('charset', encoding)
            formData.append('delimiter', divider ? divider : ',')
        }
        const instance = actionType ? INSTANCE_BY_ACTION_TYPE[actionType] : undefined
        if (instance) {
            formData.append('instance', instance)
        }
        if (isSelectFromFile) {
            formData.append('type', 'digitalSignage')

            if (selectedItems && uploadTypeValue === 'addToSelected') {
                const data: {
                    digitalSignageIds: number[]
                    groupIds: number[]
                } = {
                    digitalSignageIds: [],
                    groupIds: [],
                }

                selectedItems.forEach((item) => {
                    if (item.type === 'digitalSignage') {
                        data.digitalSignageIds.push(item.id)
                    }
                    if (item.type === 'group') {
                        data.groupIds.push(item.id)
                    }
                })

                formData.append('selected', JSON.stringify(data))
            }
        }

        formData.append('file', file)

        if (tokens.accessToken) {
            const mergedHeaders: { [key: string]: string } = {
                ...headers,
                Authorization: `Bearer ${tokens.accessToken}`,
            }

            try {
                const res = await fetch(helpers.getUploadUrl(uploadMethod), {
                    method: 'POST',
                    headers: { ...mergedHeaders },
                    body: formData,
                })
                if (res.ok) {
                    const body = await parseResponseBody(res)
                    onSuccess?.({ body })
                }
            } catch (e) {
                console.log(e)
            }

            setLoading(false)
        }
    }
    return {
        addFiles,
        encoding,
        handleSelectEncoding,
        handleSubmit,
        divider,
        setDivider,
        files,
        deleteFile,
        uploadRef,
        loading,
        acceptFiles,
        actionType,
        uploadTypeValue,
        setUploadTypeValue,
    }
}

export default useUpload
